// The bottom bar's + on phones and tablets (docs/plans/hold-plus.md): a tap is always Quick
// bill, as it has always been. Holding it opens the other ways to start something new, the
// current page's own first. A tap never changes meaning, so it can be pressed without looking.
import { IconTile, cx, type Area } from '@ims/ui'
import {
  Bike,
  ClipboardList,
  FileText,
  PackageCheck,
  PackagePlus,
  Plus,
  ShoppingBag,
  Truck,
  UserPlus,
  UtensilsCrossed,
  Wrench,
} from '@ims/ui/icons'
import { useEffect, useRef, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { NavLink, useLocation, useNavigate } from 'react-router'
import { useSettings } from '../hooks.ts'
import { providesServices } from '../lib/catalog.ts'
import { NewDeliveryDialog } from '../screens/deliveries.tsx'
import { NewTakeawayDialog } from '../screens/restaurant.tsx'
import { useCan } from '../workspace.tsx'

/** How long a press must last to open the menu instead of making a bill. */
const HOLD_MS = 400
/** A finger that moves this far is scrolling, not holding. */
const SLOP_PX = 10
/** Set once the person has seen the tip or used the menu (per device). */
const TIP_KEY = 'ims.holdPlusTip.v1'

interface NewThing {
  id: string
  label: string
  hint?: string
  area: Area
  icon: ReactNode
  /** An address that opens the form, or an action of its own. */
  to?: string
  run?: () => void
  /** The page whose own item this is. */
  page?: string
}

/** What may be started from here, the current page's own thing first. */
function useNewThings(start: (kind: 'takeaway' | 'delivery') => void): NewThing[] {
  const settings = useSettings()
  const canServe = useCan('waiter')
  const canStaff = useCan('staff')
  const { pathname } = useLocation()
  const restaurant = !!settings?.restaurant
  const icon = (I: typeof Plus) => <I className="size-[17px]" />
  const things: NewThing[] = []
  if (restaurant && canServe) {
    things.push(
      {
        id: 'dine',
        label: 'Dine in',
        hint: 'Pick a table',
        area: 'kitchen',
        icon: icon(UtensilsCrossed),
        to: '/tables',
      },
      {
        id: 'takeaway',
        label: 'Takeaway',
        area: 'take',
        icon: icon(ShoppingBag),
        run: () => start('takeaway'),
      },
    )
    if (settings?.delivery_product_id) {
      things.push({
        id: 'delivery',
        label: 'Delivery',
        hint: 'Needs a customer',
        area: 'insight',
        icon: icon(Bike),
        run: () => start('delivery'),
      })
    }
  }
  if (canServe) {
    things.push({
      id: 'customer',
      label: 'Customer',
      hint: 'Add customer',
      area: 'people',
      icon: icon(UserPlus),
      to: '/customers?new=1',
      page: '/customers',
    })
  }
  if (canStaff) {
    things.push(
      {
        id: 'supplier',
        label: 'Supplier',
        hint: 'Add supplier',
        area: 'buy',
        icon: icon(Truck),
        to: '/suppliers?new=1',
        page: '/suppliers',
      },
      {
        id: 'product',
        label: restaurant ? 'Menu item' : 'Product',
        hint: restaurant ? 'Add item' : 'Add product',
        area: 'stock',
        icon: icon(PackagePlus),
        to: '/products?new=1',
        page: '/products',
      },
      {
        id: 'po',
        label: 'Purchase order',
        area: 'buy',
        icon: icon(ClipboardList),
        to: '/purchase-orders/new',
        page: '/purchase-orders',
      },
      {
        id: 'receive',
        label: 'Receive stock',
        area: 'stock',
        icon: icon(PackageCheck),
        to: '/stock?receive=1',
        page: '/stock',
      },
    )
    // A shop that provides services adds them from here too (docs/plans/services.md).
    if (!restaurant && providesServices(settings)) {
      things.push({
        id: 'service',
        label: 'Service',
        hint: 'Add service',
        area: 'stock',
        icon: icon(Wrench),
        to: '/products?new=service',
        page: '/products',
      })
    }
    if (!restaurant) {
      things.push({
        id: 'quotation',
        label: 'Quotation',
        area: 'sell',
        icon: icon(FileText),
        to: '/quotations/new',
        page: '/quotations',
      })
    }
  }
  const here = (t: NewThing) =>
    !!t.page && (pathname === t.page || pathname.startsWith(`${t.page}/`))
  return [...things.filter(here), ...things.filter((t) => !here(t))]
}

function readTipSeen(): boolean {
  try {
    return localStorage.getItem(TIP_KEY) === '1'
  } catch {
    return true
  }
}

function markTipSeen() {
  try {
    localStorage.setItem(TIP_KEY, '1')
  } catch {
    // Storage blocked: the tip simply shows again next time.
  }
}

/** The + in the bottom bar: tap for Quick bill, hold for everything else that is new. */
export function HoldPlus() {
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const [open, setOpen] = useState(false)
  const [starting, setStarting] = useState<'takeaway' | 'delivery' | null>(null)
  const [tipSeen, setTipSeen] = useState(readTipSeen)
  const things = useNewThings(setStarting)
  const timer = useRef<number | undefined>(undefined)
  const origin = useRef<{ x: number; y: number } | null>(null)
  const held = useRef(false)

  const stop = () => {
    window.clearTimeout(timer.current)
    origin.current = null
  }
  const show = () => {
    setOpen(true)
    if (!tipSeen) {
      markTipSeen()
      setTipSeen(true)
    }
  }
  const choose = (t: NewThing) => {
    setOpen(false)
    if (t.run) t.run()
    else if (t.to) navigate(t.to)
  }
  const hasThings = things.length > 0

  return (
    <>
      <NavLink
        to="/bill"
        aria-label="Quick bill"
        draggable={false}
        onPointerDown={(e) => {
          if (!hasThings || (e.pointerType === 'mouse' && e.button !== 0)) return
          held.current = false
          origin.current = { x: e.clientX, y: e.clientY }
          timer.current = window.setTimeout(() => {
            held.current = true
            origin.current = null
            navigator.vibrate?.(12)
            show()
          }, HOLD_MS)
        }}
        onPointerMove={(e) => {
          const o = origin.current
          if (o && Math.hypot(e.clientX - o.x, e.clientY - o.y) > SLOP_PX) stop()
        }}
        onPointerUp={stop}
        onPointerCancel={stop}
        onPointerLeave={stop}
        // A hold that opened the menu is not also a tap.
        onClick={(e) => {
          if (held.current) {
            e.preventDefault()
            held.current = false
          }
        }}
        onContextMenu={(e) => e.preventDefault()}
        className={({ isActive }) =>
          cx(
            'group flex touch-manipulation select-none flex-col items-center gap-0.5 text-[11px] font-medium [-webkit-touch-callout:none]',
            isActive ? 'text-primary' : 'text-muted',
          )
        }
      >
        <span className="-mt-6 flex size-12 items-center justify-center rounded-card bg-primary text-on-fill shadow-card-hover transition-transform group-active:scale-95">
          <Plus className="size-6" />
        </span>
        Bill
      </NavLink>
      {hasThings && (
        // Holding is a gesture: keyboards and screen readers get a button for the same menu.
        <button type="button" className="sr-only" aria-haspopup="menu" onClick={show}>
          More ways to start something new
        </button>
      )}
      {/* The bar's backdrop blur would hold anything fixed inside it: these go to the body. */}
      {open &&
        createPortal(
          <NewMenu things={things} onChoose={choose} onClose={() => setOpen(false)} />,
          document.body,
        )}
      {!open &&
        !tipSeen &&
        hasThings &&
        pathname === '/' &&
        createPortal(
          <HoldTip
            onDone={() => {
              markTipSeen()
              setTipSeen(true)
            }}
          />,
          document.body,
        )}
      {starting === 'takeaway' && <NewTakeawayDialog onClose={() => setStarting(null)} />}
      {starting === 'delivery' && <NewDeliveryDialog onClose={() => setStarting(null)} />}
    </>
  )
}

function NewMenu({
  things,
  onChoose,
  onClose,
}: {
  things: NewThing[]
  onChoose: (t: NewThing) => void
  onClose: () => void
}) {
  const list = useRef<HTMLDivElement>(null)
  const { pathname } = useLocation()
  useEffect(() => {
    const items = () => [
      ...(list.current?.querySelectorAll<HTMLElement>('[role="menuitem"]') ?? []),
    ]
    items()[0]?.focus()
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
      if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return
      e.preventDefault()
      const all = items()
      const at = all.indexOf(document.activeElement as HTMLElement)
      const next = (at + (e.key === 'ArrowDown' ? 1 : all.length - 1)) % all.length
      all[next]?.focus()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [onClose])
  const own = (t: NewThing) =>
    !!t.page && (pathname === t.page || pathname.startsWith(`${t.page}/`))
  return (
    <div className="fixed inset-0 z-40 lg:hidden" data-stay-interactive>
      <div className="absolute inset-0" onClick={onClose} />
      <div
        ref={list}
        role="menu"
        aria-label="Start something new"
        className="absolute inset-x-0 bottom-[calc(5.25rem+env(safe-area-inset-bottom))] mx-auto w-[min(18rem,calc(100vw-2rem))] origin-bottom animate-scale-in rounded-card border border-border bg-raised p-1.5 shadow-modal"
      >
        <div className="flex justify-between px-2.5 pt-1.5 pb-1 text-xs font-semibold text-subtle">
          <span>New</span>
          <span className="font-medium">Tap + for a bill</span>
        </div>
        {things.map((t) => (
          <button
            key={t.id}
            type="button"
            role="menuitem"
            onClick={() => onChoose(t)}
            className={cx(
              'flex min-h-12 w-full items-center gap-3 rounded-xl px-2.5 py-1 text-left outline-none transition-[background-color,scale] duration-150 ease-out hover:bg-soft focus-visible:bg-soft active:scale-[0.98]',
              own(t) && 'bg-soft',
            )}
          >
            <IconTile area={t.area} size="sm">
              {t.icon}
            </IconTile>
            <span className="min-w-0 flex-1">
              <span className="block font-semibold">{t.label}</span>
              {t.hint && <span className="block text-xs text-muted">{t.hint}</span>}
            </span>
            {own(t) && <span className="text-xs font-semibold text-primary">This page</span>}
          </button>
        ))}
      </div>
    </div>
  )
}

/** Shown once on a phone, on Home: how to find the menu. */
function HoldTip({ onDone }: { onDone: () => void }) {
  return (
    <div
      role="note"
      className="fixed inset-x-0 bottom-[calc(5.5rem+env(safe-area-inset-bottom))] z-30 mx-auto w-[min(19rem,calc(100vw-2rem))] animate-fade-in rounded-card bg-ink p-3.5 text-sm text-surface shadow-modal lg:hidden"
    >
      <b className="block">Hold + for more</b>
      <span className="mt-0.5 block opacity-85">
        A customer, a supplier, a product, a purchase order… Tap still makes a bill.
      </span>
      <span className="mt-2 flex justify-end">
        <button
          type="button"
          onClick={onDone}
          className="rounded-lg bg-surface/15 px-3 py-1 font-semibold pointer-coarse:py-2 transition-[scale] duration-150 ease-out active:scale-[0.97]"
        >
          OK
        </button>
      </span>
      <span
        aria-hidden
        className="absolute -bottom-1.5 left-1/2 size-3.5 -translate-x-1/2 rotate-45 bg-ink"
      />
    </div>
  )
}
